import { ReactNode } from 'react';
import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { reloadTabThunk, secondaryCpOrderResetTabThunk } from 'state/secondaryMarket/cpOrder/cpOrder.thunks';
import type { SecondaryMarketCpOrderForm } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import { fromSecondaryMarket, fromSecondaryQuotesWorkspace } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import {
  getProductKey,
  getSecondaryMarketQuoteErrorKind,
  getSecondaryMarketQuoteErrorMessage,
} from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';
import { TabLoadingStatus } from 'state/workspaces/tabs/tabs.model';
import { getTabIsFrozen, getTabLoadingStatus } from 'state/workspaces/tabs/tabs.selector';
import type { HttpErrorKind } from 'utils/errors/errors';

type OwnProps = Readonly<{
  tabId: string;
  sidePanel: ReactNode;
}>;

type StateProps = Readonly<{
  productName: string | undefined;
  ric: string | undefined;
  hasFatalError: boolean;
  isLoading: boolean;
  validationErrors: SecondaryMarketFormError<SecondaryMarketCpOrderForm>[];
  traderLabel: string | undefined;
  bcsProductKey: BcsProductKey | undefined;
  canResetForm: boolean;
  errorKind?: HttpErrorKind;
  errorMessage?: string;
}>;

type DispatchProps = Readonly<{
  onResetForm: () => void;
  onRetryProductFetch: () => void;
}>;

export type CpOrderFormProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
  const getSecondaryMarketCpOrderValidationErrors = fromSecondaryMarket.cpOrder.form.makeGetCpOrderValidationErrors();
  const getBcsProductKey = createSelector([getProductKey], toBcsProductKey);
  return (state, { tabId }) => {
    const bcsProductKey = getBcsProductKey(state, tabId);
    const dataLoadingStatus = fromSecondaryQuotesWorkspace.getSecondaryMarketQuoteLoadingStatus(state, tabId);
    const productName = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductName(state, tabId);
    const ric = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductRic(state, tabId);
    const traderLabel = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductTraderLabel(state, tabId);
    const tabContentLoadingStatus = getTabLoadingStatus(state, tabId);
    const validationErrors = getSecondaryMarketCpOrderValidationErrors(state, tabId);
    const errorKind = getSecondaryMarketQuoteErrorKind(state, tabId);
    const isFrozen = getTabIsFrozen(state, tabId) ?? false;
    const canResetForm = !isFrozen;
    const hasFatalError = tabContentLoadingStatus === TabLoadingStatus.LoadingFailed || dataLoadingStatus === 'failed';
    const isLoading =
      tabContentLoadingStatus === TabLoadingStatus.Loading ||
      dataLoadingStatus === 'loading' ||
      dataLoadingStatus === 'not_yet';
    const errorMessage = getSecondaryMarketQuoteErrorMessage(state, tabId);
    return {
      productName,
      ric,
      hasFatalError,
      isLoading,
      validationErrors,
      traderLabel,
      bcsProductKey,
      canResetForm,
      errorKind,
      errorMessage,
    };
  };
};

export type ResetFormProps = OwnProps & StateProps & DispatchProps;

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
  return {
    onResetForm() {
      dispatch(secondaryCpOrderResetTabThunk(tabId));
    },
    onRetryProductFetch() {
      dispatch(reloadTabThunk(tabId));
    },
  };
};

export const connectCpOrderForm = connect(mapStateToProps, mapDispatchToProps);
